import React from 'react';
import { FileText, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

interface Document {
  id: number;
  debtor_id: number;
  document_type: string;
  document_name?: string;
  document_number?: string;
  file_url?: string;
  status?: string;
  created_date?: string;
}

interface DocumentSearchTableProps {
  documents: Document[];
  loading: boolean;
  getDebtorInfo: (debtorId: number) => { name: string; cif: string };
}

const documentTypeLabels: Record<string, string> = {
  ktp: 'KTP',
  kk: 'Kartu Keluarga',
  npwp: 'NPWP',
  slip_gaji: 'Slip Gaji',
  rekening_koran: 'Rekening Koran',
  sertifikat: 'Sertifikat',
  bpkb: 'BPKB',
  akta_nikah: 'Akta Nikah',
  surat_keterangan_usaha: 'Surat Keterangan Usaha',
  laporan_keuangan: 'Laporan Keuangan',
  slik: 'SLIK',
  foto: 'Foto',
  other: 'Lainnya',
};

const statusColors: Record<string, string> = {
  valid: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  pending_verification: 'bg-amber-100 text-amber-700',
};

const statusLabels: Record<string, string> = {
  valid: 'Valid',
  expired: 'Kadaluarsa',
  pending_verification: 'Pending Verifikasi',
};

export default function DocumentSearchTable({
  documents,
  loading,
  getDebtorInfo,
}: DocumentSearchTableProps) {
  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold">Hasil Pencarian</h3>
        <span className="px-2 py-1 bg-gray-100 rounded-full text-sm">
          {documents.length} dokumen
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Debitur</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                Jenis Dokumen
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                Nama Dokumen
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">No. Dokumen</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                Tanggal Unggah
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-4 py-3">
                      <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 w-28 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Tidak ada dokumen yang ditemukan</p>
                </td>
              </tr>
            ) : (
              documents.map(doc => {
                const debtor = getDebtorInfo(doc.debtor_id);
                return (
                  <tr key={doc.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{debtor.name}</p>
                        <p className="text-sm text-gray-500">CIF: {debtor.cif}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 border rounded-full text-xs">
                        {documentTypeLabels[doc.document_type] || doc.document_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">{doc.document_name || '-'}</td>
                    <td className="px-4 py-3 font-mono text-sm">{doc.document_number || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {doc.created_date
                        ? format(new Date(doc.created_date), 'd MMM yyyy', { locale: id })
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[doc.status || ''] || 'bg-gray-100'}`}
                      >
                        {statusLabels[doc.status || ''] || doc.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {doc.file_url && (
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <button className="p-2 hover:bg-gray-100 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
